import _sortBy from 'lodash/sortBy';
import resolve from '../../../modules/api/resolve';
import { apiPost, apiStore, generateFormData } from '../../../modules/apiHelper';
import { isStaleData, now } from '../../../modules/dataHelper';

/**
 * Helper: fetch modules by status
 */
const getModulesByStatus = async (endpoint, programid) =>
  resolve(apiPost(endpoint, generateFormData({ progid: programid }), 63)).then((data) => {
    if (!data) return false;
    return data[endpoint] || [];
  });

/**
 * Helper: sort modules chronologically
 */
const sortModulesByStartDate = (modules) =>
  _sortBy(modules, (m) => new Date(m.start).getTime());

export default {
  /**
   * Load all program modules (ended, active, upcoming)
   */
  async getProgramModules({ rootGetters }, [ProgramId, refresh = false]) {
    const programid = ProgramId || rootGetters.userProgramId;
    if (!programid) return false;

    const CACHE_KEY = `modules/${programid}`;

    const modules = (await apiStore.getItem(CACHE_KEY)) || [0, null];
    const cached = modules[1];
    const cachedModulesAreOkay =
      cached &&
      (cached[0]?.length || cached[1]?.length || cached[2]?.length);

    if (!refresh && !isStaleData(modules[0]) && cachedModulesAreOkay) {
      return cached;
    }

    const [ended, active, upcoming] = await Promise.all([
      getModulesByStatus('prog_goal_end', programid),
      getModulesByStatus('prog_goal_act', programid),
      getModulesByStatus('prog_goal_inactive', programid),
    ]);

    const allOk = [ended, active, upcoming].every((m) => !!m);
    const allModules = [
      sortModulesByStartDate(ended || []),
      sortModulesByStartDate(active || []),
      sortModulesByStartDate(upcoming || []),
    ];

    if (allOk) {
      apiStore.setItem(CACHE_KEY, [now(), allModules]);
    }

    return allModules;
  },

  /**
   * Load only active modules
   */
  async getActiveProgramModules({ rootGetters }, [ProgramId, refresh = false]) {
    const programid = ProgramId || rootGetters.userProgramId;
    if (!programid) return false;

    const CACHE_KEY = `modules/${programid}/active`;

    const modules = (await apiStore.getItem(CACHE_KEY)) || [0, []];
    const cached = modules[1];
    const cachedOkay = cached && cached?.length > 0;

    if (!refresh && !isStaleData(modules[0]) && cachedOkay) {
      return cached;
    }

    const activeModules = await getModulesByStatus('prog_goal_act', programid);
    const sorted = sortModulesByStartDate(activeModules || []);

    await apiStore.setItem(CACHE_KEY, [now(), sorted]);

    return sorted;
  },

  /**
   * Load only upcoming modules
   */
  async getUpcomingProgramModules({ rootGetters }, [ProgramId, refresh = false]) {
    const programid = ProgramId || rootGetters.userProgramId;
    if (!programid) return false;

    const CACHE_KEY = `modules/${programid}/upcoming`;

    const modules = (await apiStore.getItem(CACHE_KEY)) || [0, []];
    const cached = modules[1];
    const cachedOkay = cached && cached.length > 0;

    if (!refresh && !isStaleData(modules[0]) && cachedOkay) {
      return cached[1] || [];
    }

    const upcomingModules = await getModulesByStatus('prog_goal_inactive', programid);
    const sorted = sortModulesByStartDate(upcomingModules || []);

    await apiStore.setItem(CACHE_KEY, [now(), sorted]);

    return sorted;
  },

  /**
   * GET ALL PROGRAM MODULES, SORTED BY TASKS UNDONE FIRST
   */
  async getSelfMentorshipProgramModules({ dispatch, rootGetters }, [ProgramId, refresh = false]) {
    const programid = ProgramId || rootGetters.userProgramId;
    if (!programid) return false;

    const CACHE_KEY = `modules/${programid}/self-mentorship`;

    // Sort modules by completed status
    const sortByCompleted = (modules) => modules.sort((a, b) => a.completed - b.completed);

    const modules = (await apiStore.getItem(CACHE_KEY)) || [0, []];
    const cached = modules[1];
    const cachedOkay = cached && cached.length > 0;

    if (!refresh && !isStaleData(modules[0]) && cachedOkay) {
      return sortByCompleted(cached || []);
    }

    const allModules = await getModulesByStatus('prog_goal_list', programid);
    const sorted = sortModulesByStartDate(allModules || []);

    // get task(s) for each module
    const tasks = await dispatch('getMyProgramTasks', true);
    if (!tasks) return [];

    const mappedModules = sorted.map((module) => {
      const moduleTasks = rootGetters.getTasks.filter((task) => task.goalid == module.id);

      const allTasksDone = moduleTasks.length ? moduleTasks.every((task) => task.status != '0') : false;

      return {
        ...module,
        tasks: moduleTasks,
        completed: allTasksDone,
      };
    });

    if (mappedModules?.length > 0) {
      await apiStore.setItem(CACHE_KEY, [now(), mappedModules]);
    }

    // sort by completed status
    return sortByCompleted(mappedModules);
  },

  async clearSelfMentorshipProgramModulesCache({ rootGetters }, ProgramId) {
    const programid = ProgramId || rootGetters.userProgramId;
    if (!programid) return false;

    const CACHE_KEY = `modules/${programid}/self-mentorship`;
    await apiStore.removeItem(CACHE_KEY);
    return true;
  },
};
